import type { User } from 'firebase/auth';
import {
  getCurrentUserId,
  setCurrentUserId,
  resetUserIdentity,
  resetToFreshLocalLearner,
  isLocalLearner,
} from '../identity/learnerIdentity';
import { migrationService } from './migrationService';
import { syncQueueService } from './syncQueueService';
import { diagnosticJournal } from '../../services/diagnosticJournal';
import { db, CallFoundryDexie } from '../db';
import { localAudioRepository } from '../../domains/audio/storage/localAudioRepository';

export interface AuthState {
  user: User | null;
  userId: string;
  isAnonymous: boolean;
  isLoading: boolean;
}

const AUTH_ACTIVE_STORAGE_KEY = 'callfoundry_auth_active';

/**
 * Translates Firebase Auth error codes into clean, non-technical, human-friendly messages.
 * Strips all internal Firebase SDK identifiers and runtime jargon.
 */
export function getFriendlyAuthErrorMessage(error: unknown): string {
  if (!error) return 'An unexpected error occurred. Please try again.';
  const code = (error as { code?: string }).code || '';

  switch (code) {
    case 'auth/invalid-credential':
    case 'auth/user-not-found':
    case 'auth/wrong-password':
      return 'The email or password you entered is incorrect. Please check your credentials and try again.';
    case 'auth/email-already-in-use':
      return 'An account already exists with this email address. Please sign in instead.';
    case 'auth/weak-password':
      return 'Please choose a stronger password (at least 6 characters).';
    case 'auth/invalid-email':
      return 'Please enter a valid email address.';
    case 'auth/user-disabled':
      return 'This account has been disabled. Please contact support.';
    case 'auth/too-many-requests':
      return 'Too many attempts. Access has been temporarily restricted. Please wait a few moments and try again.';
    case 'auth/network-request-failed':
      return 'Unable to reach the service. Please check your network connection and try again.';
    case 'auth/requires-recent-login':
      return 'For security, please sign in again before performing this sensitive action.';
    case 'auth/operation-not-allowed':
      return 'Email and password sign-in is currently unavailable. Please try again later.';
    default:
      if (error instanceof Error && error.message) {
        const cleaned = error.message
          .replace(/^Firebase:\s*/i, '')
          .replace(/\s*\([a-z0-9/_-]+\)\.?$/i, '')
          .trim();
        // Disallow account-existence reveals
        if (/user[- _]not[- _]found|no account|not registered|user does not exist/i.test(cleaned)) {
          return 'The email or password you entered is incorrect. Please check your credentials and try again.';
        }
        if (cleaned.startsWith('auth/')) {
          return 'An unexpected error occurred. Please try again.';
        }
        return cleaned || 'An unexpected error occurred. Please try again.';
      }
      return 'An unexpected error occurred. Please try again.';
  }
}

/**
 * Isolates local Dexie state when an authenticated account signs out.
 * Ensures User B never sees or inherits User A's private training attempts or events.
 */
export async function isolateSignedOutSession(accountUid: string, database: CallFoundryDexie = db): Promise<void> {
  try {
    await database.transaction(
      'rw',
      [
        database.attempts,
        database.masteryProfiles,
        database.events,
        database.syncMetadata,
        database.learnerProfiles,
      ],
      async () => {
        // 1. Clear current active attempt and mastery views
        await database.attempts.clear();
        await database.masteryProfiles.clear();

        // 2. Remove ONLY already-synced events for this account from local active table.
        // (Canonical records safely exist in the Cloud Firestore replica and will restore upon sign-in).
        // CRITICAL INVARIANT: Pending offline events for this account must be PRESERVED in Dexie,
        // tagged with accountUid, so offline work is not lost when signing out!
        const accountMeta = await database.syncMetadata.where('accountUid').equals(accountUid).toArray();
        for (const meta of accountMeta) {
          if (meta.status === 'synced') {
            await database.events.delete(meta.eventId);
            await database.syncMetadata.delete(meta.eventId);
          }
        }

        // 3. Clear profile for this account
        await database.learnerProfiles.delete(accountUid);
      }
    );
  } catch (err) {
    await diagnosticJournal.warn('AuthService', 'Session isolation warning on sign out', {
      error: String(err),
    });
  }
}

export class AuthService {
  private currentUser: User | null = null;
  private listeners: Array<(state: AuthState) => void> = [];
  private isLoading = true;
  private initPromise: Promise<void> | null = null;
  private skipGuestMigrationOnNextAuth = false;

  constructor() {
    // Only auto-initialize Firebase Auth if an account was previously active on this device
    if (typeof localStorage !== 'undefined' && localStorage.getItem(AUTH_ACTIVE_STORAGE_KEY) === 'true') {
      void this.ensureInitialized();
    } else {
      this.isLoading = false;
    }
  }

  /**
   * Lazily loads Firebase Auth SDK on demand.
   * Ensures purely local learners do not eagerly download Firebase bundles.
   */
  async ensureInitialized(): Promise<void> {
    if (this.initPromise) return this.initPromise;

    this.initPromise = (async () => {
      try {
        const { onAuthStateChanged } = await import('firebase/auth');
        const { getFirebaseAuth } = await import('../../infrastructure/firebase/firebaseClient');
        const auth = getFirebaseAuth();

        onAuthStateChanged(auth, async (user) => {
          this.currentUser = user;
          this.isLoading = false;

          if (user) {
            if (typeof localStorage !== 'undefined') {
              localStorage.setItem(AUTH_ACTIVE_STORAGE_KEY, 'true');
            }
            const previousId = getCurrentUserId();
            setCurrentUserId(user.uid);

            // If transitioning from local learner identity, check explicit merge consent
            if (isLocalLearner(previousId)) {
              if (this.skipGuestMigrationOnNextAuth) {
                this.skipGuestMigrationOnNextAuth = false;
                // INVARIANT: Never destroy guest progress! Guest events remain intact in Dexie under their local identity.
                // Clear presentation views so the authenticated account starts with its own clean workspace.
                await db.transaction('rw', [db.attempts, db.masteryProfiles, db.learnerProfiles], async () => {
                  await db.attempts.clear();
                  await db.masteryProfiles.clear();
                  await db.learnerProfiles.clear();
                });
                void syncQueueService.pullRemoteEvents(user.uid);
              } else {
                try {
                  await migrationService.promoteAnonymousEventsToAccount(user.uid);
                  // Trigger push of promoted events and pull of existing remote events
                  void syncQueueService.processSyncQueue();
                  void syncQueueService.pullRemoteEvents(user.uid);
                } catch (err) {
                  await diagnosticJournal.warn('AuthService', 'Migration on login failed', {
                    error: String(err),
                  });
                }
              }
            } else {
              // Returning or switched user:
              // 1. Process any pending offline events for this account
              void syncQueueService.processSyncQueue();
              // 2. Pull existing remote events
              void syncQueueService.pullRemoteEvents(user.uid);
            }
          } else {
            if (typeof localStorage !== 'undefined') {
              localStorage.removeItem(AUTH_ACTIVE_STORAGE_KEY);
            }
            // Signed out: revert to local anonymous identity
            // INVARIANT: Never erase local Dexie database
            resetUserIdentity();
          }

          this.notify();
        });
      } catch {
        // In offline / unconfigured mode
        this.isLoading = false;
        this.notify();
      }
    })();

    return this.initPromise;
  }

  subscribe(listener: (state: AuthState) => void): () => void {
    this.listeners.push(listener);
    listener(this.getState());
    return () => {
      this.listeners = this.listeners.filter((l) => l !== listener);
    };
  }

  private notify(): void {
    const state = this.getState();
    for (const listener of this.listeners) {
      try {
        listener(state);
      } catch {
        // ignore
      }
    }
  }

  getState(): AuthState {
    return {
      user: this.currentUser,
      userId: this.currentUser ? this.currentUser.uid : getCurrentUserId(),
      isAnonymous: !this.currentUser,
      isLoading: this.isLoading,
    };
  }

  async register(
    email: string,
    password: string,
    displayName?: string,
    mergeGuestProgress: boolean = true
  ): Promise<User> {
    await this.ensureInitialized();
    try {
      this.skipGuestMigrationOnNextAuth = !mergeGuestProgress;
      const { createUserWithEmailAndPassword, updateProfile } = await import('firebase/auth');
      const { getFirebaseAuth } = await import('../../infrastructure/firebase/firebaseClient');
      const auth = getFirebaseAuth();
      const cred = await createUserWithEmailAndPassword(auth, email, password);

      if (displayName && displayName.trim()) {
        try {
          await updateProfile(cred.user, { displayName: displayName.trim() });
        } catch {
          // non-blocking
        }
      }

      if (typeof localStorage !== 'undefined') {
        localStorage.setItem(AUTH_ACTIVE_STORAGE_KEY, 'true');
      }

      await diagnosticJournal.info('AuthService', 'User registered successfully', {
        uid: cred.user.uid,
      });

      return cred.user;
    } catch (err) {
      this.skipGuestMigrationOnNextAuth = false;
      throw new Error(getFriendlyAuthErrorMessage(err), { cause: err });
    }
  }

  async signIn(
    email: string,
    password: string,
    options?: { mergeGuestProgress?: boolean }
  ): Promise<User> {
    await this.ensureInitialized();
    if (options && typeof options.mergeGuestProgress === 'boolean') {
      this.skipGuestMigrationOnNextAuth = !options.mergeGuestProgress;
    }
    try {
      const { signInWithEmailAndPassword } = await import('firebase/auth');
      const { getFirebaseAuth } = await import('../../infrastructure/firebase/firebaseClient');
      const auth = getFirebaseAuth();
      const cred = await signInWithEmailAndPassword(auth, email, password);

      if (typeof localStorage !== 'undefined') {
        localStorage.setItem(AUTH_ACTIVE_STORAGE_KEY, 'true');
      }

      await diagnosticJournal.info('AuthService', 'User signed in successfully', {
        uid: cred.user.uid,
      });

      return cred.user;
    } catch (err) {
      this.skipGuestMigrationOnNextAuth = false;
      throw new Error(getFriendlyAuthErrorMessage(err), { cause: err });
    }
  }

  async signOut(): Promise<void> {
    await this.ensureInitialized();
    const previousUid = this.currentUser?.uid;

    // Flush pending sync queue before signing out if online
    try {
      if (typeof navigator !== 'undefined' && navigator.onLine) {
        await syncQueueService.processSyncQueue();
      }
    } catch {
      // Best-effort flush
    }

    // Isolate previous account's session from next user
    if (previousUid) {
      await isolateSignedOutSession(previousUid);
    }

    try {
      const { signOut } = await import('firebase/auth');
      const { getFirebaseAuth } = await import('../../infrastructure/firebase/firebaseClient');
      const auth = getFirebaseAuth();
      await signOut(auth);
    } catch {
      // Fallback
    }

    if (typeof localStorage !== 'undefined') {
      localStorage.removeItem(AUTH_ACTIVE_STORAGE_KEY);
    }

    // Check if there are preserved guest events in Dexie
    const allEvents = await db.events.toArray();
    const guestEvents = allEvents.filter((e) => isLocalLearner(e.originLearnerId));

    if (guestEvents.length > 0) {
      // Restore the persistent guest learner ID
      const preservedGuestId = guestEvents[0].originLearnerId;
      setCurrentUserId(preservedGuestId);
      // Rebuild active attempts and mastery profiles from the preserved guest events
      const { rebuildDerivedStateFromEvents } = await import('../../domains/mastery/derivedStateRebuilder');
      await rebuildDerivedStateFromEvents(guestEvents, db);
    } else {
      // Reset to a brand new local learner ID for multi-account safety
      resetToFreshLocalLearner();
    }

    this.currentUser = null;
    this.notify();

    await diagnosticJournal.info('AuthService', 'User signed out; session isolated; guest workspace restored if present');
  }

  async sendPasswordReset(email: string): Promise<void> {
    const trimmed = (email || '').trim();
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!trimmed || !emailRegex.test(trimmed)) {
      throw new Error('Please enter a valid email address.');
    }
    await this.ensureInitialized();
    try {
      const { sendPasswordResetEmail } = await import('firebase/auth');
      const { getFirebaseAuth } = await import('../../infrastructure/firebase/firebaseClient');
      const auth = getFirebaseAuth();
      await sendPasswordResetEmail(auth, trimmed);
      await diagnosticJournal.info('AuthService', 'Password reset requested', { email: trimmed });
    } catch (err: unknown) {
      const code = (err as { code?: string })?.code;
      const message = err instanceof Error ? err.message : '';
      if (code === 'auth/user-not-found' || /user-not-found|no user record/i.test(message)) {
        // Email enumeration protection: intentionally resolve without error to protect account privacy
        await diagnosticJournal.info('AuthService', 'Password reset requested for unregistered email (suppressed for privacy)', { email: trimmed });
        return;
      }
      throw new Error(getFriendlyAuthErrorMessage(err), { cause: err });
    }
  }

  async updateDisplayName(displayName: string): Promise<void> {
    await this.ensureInitialized();
    if (!this.currentUser) {
      throw new Error('You must be signed in to update your profile name.');
    }

    try {
      const { updateProfile } = await import('firebase/auth');
      await updateProfile(this.currentUser, { displayName });

      // Update local profile
      const profile = await db.learnerProfiles.get(this.currentUser.uid);
      if (profile) {
        await db.learnerProfiles.update(this.currentUser.uid, { displayName });
      }

      // Update remote profile in Firestore if online
      try {
        const { doc, setDoc, serverTimestamp } = await import('firebase/firestore');
        const { getFirebaseFirestore } = await import('../../infrastructure/firebase/firebaseClient');
        const firestore = getFirebaseFirestore();
        const profileRef = doc(firestore, 'users', this.currentUser.uid, 'profile', 'main');
        await setDoc(profileRef, { displayName, updatedAt: serverTimestamp() }, { merge: true });
      } catch {
        // Offline / best-effort
      }

      this.notify();
      await diagnosticJournal.info('AuthService', 'Display name updated', { displayName });
    } catch (err) {
      throw new Error(getFriendlyAuthErrorMessage(err), { cause: err });
    }
  }

  /**
   * Clears all local training data, audio recordings, and resets the device to clean slate.
   * Does NOT touch Cloud Firestore or the user's remote account.
   */
  async clearDeviceData(): Promise<void> {
    try {
      await db.transaction(
        'rw',
        [
          db.events,
          db.attempts,
          db.masteryProfiles,
          db.syncQueue,
          db.syncMetadata,
          db.learnerProfiles,
          db.trainingSessions,
        ],
        async () => {
          await db.events.clear();
          await db.attempts.clear();
          await db.masteryProfiles.clear();
          await db.syncQueue.clear();
          await db.syncMetadata.clear();
          await db.learnerProfiles.clear();
          await db.trainingSessions.clear();
        }
      );

      await localAudioRepository.clearAllRecordings();
      resetToFreshLocalLearner();
      this.notify();
      await diagnosticJournal.info('AuthService', 'Device data cleared');
    } catch (err) {
      throw new Error(`Failed to clear device data: ${err instanceof Error ? err.message : String(err)}`, { cause: err });
    }
  }

  /**
   * Deletes all remote synchronized data from Cloud Firestore.
   * Leaves local device training data and user Auth account intact.
   */
  async deleteSynchronizedData(): Promise<void> {
    await this.ensureInitialized();
    if (!this.currentUser) {
      throw new Error('You must be signed in to manage synchronized cloud data.');
    }

    const uid = this.currentUser.uid;

    try {
      const { collection, getDocs, deleteDoc, doc } = await import('firebase/firestore');
      const { getFirebaseFirestore } = await import('../../infrastructure/firebase/firebaseClient');
      const firestore = getFirebaseFirestore();

      // 1. Delete all events in users/{uid}/events
      const eventsRef = collection(firestore, 'users', uid, 'events');
      const eventsSnap = await getDocs(eventsRef);
      for (const d of eventsSnap.docs) {
        await deleteDoc(d.ref);
      }

      // 2. Delete all profile docs in users/{uid}/profile
      const profileRef = collection(firestore, 'users', uid, 'profile');
      const profileSnap = await getDocs(profileRef);
      for (const d of profileSnap.docs) {
        await deleteDoc(d.ref);
      }

      // 3. Delete users/{uid} root doc
      await deleteDoc(doc(firestore, 'users', uid));

      // Reset local sync metadata status for this user
      await db.transaction('rw', [db.syncMetadata, db.syncQueue], async () => {
        const metas = await db.syncMetadata.where('accountUid').equals(uid).toArray();
        for (const m of metas) {
          await db.syncMetadata.update(m.eventId, {
            status: 'pending',
          });
        }
      });

      await diagnosticJournal.info('AuthService', 'Synchronized cloud data deleted', { uid });
    } catch (err) {
      throw new Error(getFriendlyAuthErrorMessage(err), { cause: err });
    }
  }

  /**
   * Fully deletes the user's account:
   * 1. Deletes all remote Firestore documents in /users/{uid}
   * 2. Deletes the Firebase Auth user
   * 3. Clears local device data
   */
  async deleteAccount(): Promise<void> {
    await this.ensureInitialized();
    if (!this.currentUser) {
      throw new Error('You must be signed in to delete your account.');
    }

    const userToDelete = this.currentUser;
    const uid = userToDelete.uid;

    try {
      // 1. Delete remote Firestore data
      try {
        const { collection, getDocs, deleteDoc, doc } = await import('firebase/firestore');
        const { getFirebaseFirestore } = await import('../../infrastructure/firebase/firebaseClient');
        const firestore = getFirebaseFirestore();

        const eventsRef = collection(firestore, 'users', uid, 'events');
        const eventsSnap = await getDocs(eventsRef);
        for (const d of eventsSnap.docs) {
          await deleteDoc(d.ref);
        }

        const profileRef = collection(firestore, 'users', uid, 'profile');
        const profileSnap = await getDocs(profileRef);
        for (const d of profileSnap.docs) {
          await deleteDoc(d.ref);
        }

        await deleteDoc(doc(firestore, 'users', uid));
      } catch {
        // Continue with account deletion even if Firestore docs were already absent
      }

      // 2. Delete Firebase Auth account
      const { deleteUser } = await import('firebase/auth');
      await deleteUser(userToDelete);

      // 3. Clear local storage flag and local data
      if (typeof localStorage !== 'undefined') {
        localStorage.removeItem(AUTH_ACTIVE_STORAGE_KEY);
      }
      await this.clearDeviceData();
      this.currentUser = null;
      this.notify();

      await diagnosticJournal.info('AuthService', 'User account fully deleted', { uid });
    } catch (err) {
      throw new Error(getFriendlyAuthErrorMessage(err), { cause: err });
    }
  }
}

export const authService = new AuthService();
